import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { LANE_ICONS } from "@/pages/HomePage";

const inputCls = "w-full rounded-xl border border-neutral-700 bg-black px-4 py-3 text-sm text-stone-100 outline-none focus:border-cyan-500";
const fmtK = (n) => (n >= 1000 ? `$${Math.round(n / 1000)}K` : `$${n}`);

export default function RequestPage() {
  const [params] = useSearchParams();
  const [lanes, setLanes] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [step, setStep] = useState(params.get("lane") ? 1 : 0);
  const [form, setForm] = useState({ lane: params.get("lane") || "", name: "", email: "", phone: "", company: "", event_date: "", city: "", guests: "", budget: "", artist: "", details: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => { api.get("/lanes").then((r) => { setLanes(r.data.lanes); setBudgets(r.data.budgets); }).catch(() => {}); }, []);
  const lane = lanes.find((l) => l.id === form.lane);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const has = (f) => lane?.fields?.includes(f);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return toast.error("Name and email are required.");
    setBusy(true);
    try {
      const { data } = await api.post("/requests", form);
      setDone(data.reference);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (done) return (
    <div className="mx-auto max-w-xl px-4 py-28 text-center" data-testid="request-success">
      <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-cyan-500/20"><Check className="h-8 w-8 text-cyan-400" /></div>
      <span className="eyebrow mt-6 block">Request received</span>
      <h1 className="mt-2 font-display text-4xl uppercase text-chrome">The decision is made.</h1>
      <div className="mt-3 font-display text-2xl tracking-wider text-gold" data-testid="request-reference">{done}</div>
      <p className="mt-4 text-neutral-400">An EON producer reviews your brief personally and returns a scoped proposal within one business day. Confirmation is in your inbox.</p>
      <Link to="/" className="mt-8 inline-block font-cond font-bold uppercase text-neutral-400 hover:text-cyan-400">← Back home</Link>
    </div>
  );

  return (
    <div className="mx-auto max-w-5xl px-4 py-14 sm:px-6 md:py-20" data-testid="request-page">
      <span className="eyebrow">Start your request</span>
      <h1 className="mt-2 font-display text-4xl uppercase text-chrome sm:text-5xl">Brief EON in <span className="text-gold">three minutes.</span></h1>
      <div className="mt-8 flex gap-2">
        {["Lane", "Details", "Contact"].map((s, i) => (
          <div key={s} className={`h-1.5 flex-1 rounded-full ${i <= step ? "bg-cyan-500" : "bg-stone-800"}`} data-testid={`step-bar-${i}`} />
        ))}
      </div>

      {step === 0 && (
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="lane-picker">
          {lanes.map((l, i) => {
            const Icon = LANE_ICONS[l.id];
            return (
              <motion.button key={l.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                onClick={() => { setForm((f) => ({ ...f, lane: l.id })); setStep(1); }} data-testid={`pick-lane-${l.id}`}
                className="glass flex flex-col rounded-2xl p-6 text-left transition-all hover:-translate-y-1 hover:border-cyan-500/60">
                <Icon className="h-6 w-6 text-cyan-400" />
                <h3 className="mt-3 font-cond text-xl font-extrabold uppercase text-white">{l.name}</h3>
                <p className="mt-2 flex-1 text-sm text-neutral-400">{l.pitch}</p>
                <div className="mt-4 font-display text-xl text-gold">from {fmtK(l.starting)}</div>
              </motion.button>
            );
          })}
        </div>
      )}

      {step === 1 && lane && (
        <div className="mt-10 grid gap-8 md:grid-cols-12">
          <div className="glass rounded-3xl p-6 md:col-span-5">
            <span className="eyebrow">{lane.tag}</span>
            <h2 className="mt-2 font-cond text-2xl font-extrabold uppercase text-white">{lane.name}</h2>
            <div className="mt-2 font-display text-2xl text-gold">from {fmtK(lane.starting)} <span className="font-mono-label text-[10px] uppercase tracking-widest text-neutral-500">{lane.unit}</span></div>
            <h4 className="mt-6 font-mono-label text-[10px] uppercase tracking-widest text-neutral-400">What's included</h4>
            <ul className="mt-2 space-y-1.5">{lane.deliverables.map((d) => <li key={d} className="flex gap-2 text-sm text-neutral-300"><Check className="mt-0.5 h-4 w-4 text-cyan-500" />{d}</li>)}</ul>
            <button onClick={() => setStep(0)} className="mt-6 flex items-center gap-1 text-sm text-neutral-500 hover:text-cyan-400" data-testid="change-lane-btn"><ArrowLeft className="h-4 w-4" /> Change lane</button>
          </div>
          <div className="md:col-span-7">
            <h3 className="font-display text-2xl uppercase text-chrome">Tell us what you want</h3>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {has("artist") && <input placeholder="Artist(s) you want" value={form.artist} onChange={set("artist")} data-testid="req-artist" className={`${inputCls} sm:col-span-2`} />}
              {has("date") && <input type="date" value={form.event_date} onChange={set("event_date")} data-testid="req-date" className={inputCls} />}
              {has("city") && <input placeholder="City / venue" value={form.city} onChange={set("city")} data-testid="req-city" className={inputCls} />}
              {has("guests") && <input placeholder="Expected guests" value={form.guests} onChange={set("guests")} data-testid="req-guests" className={inputCls} />}
              <select value={form.budget} onChange={set("budget")} data-testid="req-budget" className={inputCls}>
                <option value="">Budget range</option>
                {budgets.map((b) => <option key={b}>{b}</option>)}
              </select>
              <textarea rows={5} placeholder={`What does success look like? ${lane.needs.join(" · ")}`} value={form.details} onChange={set("details")} data-testid="req-details" className={`${inputCls} sm:col-span-2`} />
            </div>
            <button onClick={() => setStep(2)} data-testid="req-next-btn" className="btn-gold mt-5 flex items-center gap-2 rounded-full px-7 py-3.5 font-cond font-bold uppercase">Continue <ArrowRight className="h-4 w-4" /></button>
          </div>
        </div>
      )}

      {step === 2 && (
        <form onSubmit={submit} className="glass mt-10 max-w-2xl rounded-3xl p-6 md:p-8" data-testid="req-contact-form">
          <h3 className="font-display text-2xl uppercase text-chrome">Where do we send the proposal?</h3>
          <div className="mt-5 grid gap-3 sm:grid-cols-2">
            <input required placeholder="Your name" value={form.name} onChange={set("name")} data-testid="req-name" className={inputCls} />
            <input placeholder="Company / organization" value={form.company} onChange={set("company")} data-testid="req-company" className={inputCls} />
            <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="req-email" className={inputCls} />
            <input placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="req-phone" className={inputCls} />
          </div>
          <div className="mt-5 flex items-center gap-3">
            <button type="button" onClick={() => setStep(1)} className="rounded-full border border-neutral-700 px-5 py-3 font-cond font-bold uppercase text-neutral-300" data-testid="req-back-btn">Back</button>
            <button type="submit" disabled={busy} data-testid="req-submit-btn" className="btn-gold flex flex-1 items-center justify-center gap-2 rounded-full py-3.5 font-display text-lg uppercase disabled:opacity-60">
              {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Send My Brief"}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
